import type { CsvQuestionInputItem } from "~/components/difyAPITest/difyAPITest.types";
import { parseCsvRows } from "~/utils/csvUtils";

/**
 * CSV入力で必須とするヘッダー名
 */
const DIFY_INPUT_CSV_HEADERS = {
  question: "質問",
  expectedAnswer: "回答",
} as const;

/**
 * CSV文字列から質問入力一覧を生成する
 * (Papaparseを反映後は直接Fileオブジェクトを読み込むようにする)
 * @param csvText CSV文字列
 * @return 「質問」と「回答」の質問入力一覧
 */
export const parseCsvQuestionInputItems = (csvText: string): CsvQuestionInputItem[] => {
  const rows = parseCsvRows(csvText);

  if (rows.length === 0) {
    throw new Error("CSVファイルが空です。");
  }

  const headers = (rows[0] ?? []).map((header) => header.trim());
  const questionIndex = headers.findIndex((header) => header === DIFY_INPUT_CSV_HEADERS.question);
  const expectedAnswerIndex = headers.findIndex(
    (header) => header === DIFY_INPUT_CSV_HEADERS.expectedAnswer
  );

  if (questionIndex < 0 || expectedAnswerIndex < 0) {
    throw new Error("CSVファイルに「質問」と「回答」列が必要です。");
  }

  const questionItems: CsvQuestionInputItem[] = [];

  rows.slice(1).forEach((row, index) => {
    const question = row[questionIndex] ?? "";
    const expectedAnswer = row[expectedAnswerIndex] ?? "";
    const normalizedQuestion = question.trim();
    const normalizedExpectedAnswer = expectedAnswer.trim();

    if (!normalizedQuestion && !normalizedExpectedAnswer) {
      return;
    }

    if (!normalizedQuestion || !normalizedExpectedAnswer) {
      throw new Error(`${index + 2}行目の「質問」または「回答」が空です。`);
    }

    questionItems.push({
      question: normalizedQuestion,
      expectedAnswer: normalizedExpectedAnswer,
    });
  });

  if (questionItems.length === 0) {
    throw new Error("CSVファイルに有効なデータがありません。");
  }

  return questionItems;
};

/**
 * Fileオブジェクトからテキストを読み込む
 * @param file 読み込むファイル
 * @return ファイル内容
 */
const readTextFromFile = async (file: File): Promise<string> => {
  if (typeof file.text === "function") {
    return file.text();
  }

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () =>
      reject(reader.error ?? new Error("CSVファイルの読み込みに失敗しました。"));
    reader.readAsText(file);
  });
};

/**
 * CSVファイルから質問入力一覧を読み込む
 * @param file CSVファイル
 * @return 質問入力一覧
 */
export const readCsvQuestionInputFile = async (file: File): Promise<CsvQuestionInputItem[]> => {
  const csvText = await readTextFromFile(file);

  return parseCsvQuestionInputItems(csvText);
};
